import { useState, useRef, useEffect } from "react";
import { Cpu, X, Maximize2, Paperclip, Send, FileText, BookOpen, Lightbulb, AlertTriangle, Wrench, Upload, Loader2 } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import ReactMarkdown from "react-markdown";

interface AIAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStep: {
    phase: number;
    title: string;
  };
  modelNumber: string;
  /** Library manual id (preferred). The AI loads this PDF from the Repair Manual Library. */
  manualId?: string | null;
  /** Display name of the active manual (shown in the header chips). */
  manualTitle?: string | null;
  /** Legacy: direct file path in the old "repair-manuals" bucket. */
  manualUrl?: string | null;
  /** True when the attached manual is a brand-fallback (no exact model match in the library). */
  manualIsFallback?: boolean;
  /** Optional human label for the appliance's model series, e.g. "G 2xxx". Used in the fallback notice. */
  manualSeriesLabel?: string | null;
  /** Brand of the appliance, used in the fallback notice when no series label is available. */
  applianceBrand?: string | null;
  /** Called after a successful upload to the library. */
  onManualUploaded?: (manualId: string, title: string) => void;
}

type Msg = { role: "user" | "assistant"; content: string };

const quickQuestions = [
  "Is this step safe to perform?",
  "What tools do I need for this step?",
  "How long should this step take?",
  "What if I encounter an error?",
  "Can you show me the procedure?",
  "What are common mistakes here?",
];

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manual-chat`;

async function streamChat({
  messages,
  manualId,
  manualUrl,
  modelNumber,
  currentStep,
  onDelta,
  onDone,
  onError,
}: {
  messages: Msg[];
  manualId?: string | null;
  manualUrl?: string | null;
  modelNumber: string;
  currentStep: { phase: number; title: string };
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (err: string) => void;
}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    onError("Not authenticated");
    return;
  }

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ messages, manualId, manualUrl, modelNumber, currentStep }),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({ error: "Unknown error" }));
    onError(errorData.error || `Error ${resp.status}`);
    return;
  }

  if (!resp.body) {
    onError("No response body");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let textBuffer = "";
  let streamDone = false;

  while (!streamDone) {
    const { done, value } = await reader.read();
    if (done) break;
    textBuffer += decoder.decode(value, { stream: true });

    let newlineIndex: number;
    while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
      let line = textBuffer.slice(0, newlineIndex);
      textBuffer = textBuffer.slice(newlineIndex + 1);

      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;

      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        streamDone = true;
        break;
      }

      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch {
        textBuffer = line + "\n" + textBuffer;
        break;
      }
    }
  }

  if (textBuffer.trim()) {
    for (let raw of textBuffer.split("\n")) {
      if (!raw) continue;
      if (raw.endsWith("\r")) raw = raw.slice(0, -1);
      if (raw.startsWith(":") || raw.trim() === "") continue;
      if (!raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content as string | undefined;
        if (content) onDelta(content);
      } catch { /* ignore */ }
    }
  }

  onDone();
}

export function AIAssistantDialog({
  open,
  onOpenChange,
  currentStep,
  modelNumber,
  manualId,
  manualTitle,
  manualUrl,
  manualIsFallback,
  manualSeriesLabel,
  applianceBrand,
  onManualUploaded,
}: AIAssistantDialogProps) {
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const hasManual = !!(manualId || manualUrl);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (text?: string) => {
    const content = text || inputValue.trim();
    if (!content || isLoading) return;
    setInputValue("");

    const userMsg: Msg = { role: "user", content };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setIsLoading(true);

    let assistantSoFar = "";
    const upsertAssistant = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant") {
          return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
        }
        return [...prev, { role: "assistant", content: assistantSoFar }];
      });
    };

    await streamChat({
      messages: newMessages,
      manualId,
      manualUrl,
      modelNumber,
      currentStep,
      onDelta: upsertAssistant,
      onDone: () => setIsLoading(false),
      onError: (err) => {
        toast.error(err);
        setIsLoading(false);
      },
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be less than 20MB");
      return;
    }

    setIsUploading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Not authenticated");
        return;
      }

      // Upload directly into the Repair Manual Library bucket
      const safeName = file.name.replace(/[^\w.\-]/g, "_");
      const filePath = `${session.user.id}/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from("repair-guides")
        .upload(filePath, file, { contentType: "application/pdf" });
      if (uploadError) throw uploadError;

      // Insert library row
      const companyId = await fetchCurrentCompanyId(session.user.id);
      if (!companyId) throw new Error("You're not assigned to a company yet — contact your admin.");
      const { data: row, error: insErr } = await supabase
        .from("repair_manuals")
        .insert({
          user_id: session.user.id,
          company_id: companyId,
          file_path: filePath,
          file_size: file.size,
          original_filename: file.name,
          metadata_status: "pending",
        })
        .select("id")
        .single();
      if (insErr || !row) throw insErr || new Error("Insert failed");

      toast.success(`"${file.name}" added to Repair Manual Library — extracting metadata...`);
      onManualUploaded?.(row.id, file.name);

      // Trigger background AI extraction (non-blocking)
      supabase.functions.invoke("extract-manual-metadata", {
        body: { manualId: row.id, filePath },
      }).catch((e) => console.error("metadata extraction:", e));
    } catch (err: any) {
      toast.error("Upload failed: " + (err.message || "Unknown error"));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Cpu className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">FixID AI Assistant</h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-refurbish"></span>
                  Online & Ready
                </span>
                <span>•</span>
                <span>Step {currentStep.phase}: {currentStep.title}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-muted transition-colors">
              <Maximize2 className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Chat Area */}
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {/* Welcome Message */}
          <div className="bg-muted/50 rounded-2xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Cpu className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground mb-2">
                  👋 <strong>Welcome to FixID AI Assistant!</strong>
                </p>
                <p className="text-muted-foreground mb-3">
                  I'm here to help with your <strong>{modelNumber}</strong> work. I have access to:
                </p>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                    <span><strong className="text-foreground">Repair Manual Library</strong> — shared PDFs maintained across the team</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lightbulb className="w-4 h-4 text-warning flex-shrink-0" />
                    <span><strong className="text-foreground">FixID community knowledge</strong> from thousands of refurbishments</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                    <span><strong className="text-foreground">Safety protocols</strong> and best practices</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Wrench className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span><strong className="text-foreground">Troubleshooting</strong> for issues not covered in standard docs</span>
                  </li>
                </ul>
                <div className="flex items-center gap-2 flex-wrap">
                  {hasManual ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-refurbish/10 border border-refurbish/20 rounded-lg text-xs text-refurbish">
                      <FileText className="w-3 h-3" />
                      {manualTitle ? `Library manual: ${manualTitle}` : "Repair manual loaded ✓"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-warning/10 border border-warning/20 rounded-lg text-xs text-warning">
                      <Upload className="w-3 h-3" />
                      No manual attached — pick one from the library or upload a new PDF
                    </span>
                  )}
                </div>
                {hasManual && manualIsFallback && (
                  <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20 text-xs text-warning">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      <strong>No exact manual</strong> for this model in the library — using closest{" "}
                      {manualSeriesLabel
                        ? <><strong>{manualSeriesLabel}</strong></>
                        : applianceBrand
                          ? <><strong>{applianceBrand}</strong></>
                          : "brand"}{" "}
                      manual as reference{manualTitle ? <> (<em>{manualTitle}</em>)</> : null}. Answers may not match this exact model — verify against the appliance before acting.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Messages */}
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`mb-4 ${msg.role === "user" ? "flex justify-end" : ""}`}
            >
              {msg.role === "user" ? (
                <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-3 max-w-[80%]">
                  <p className="text-sm">{msg.content}</p>
                </div>
              ) : (
                <div className="bg-muted/50 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Cpu className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 prose prose-sm max-w-none text-foreground prose-headings:text-foreground prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
            <div className="bg-muted/50 rounded-2xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Cpu className="w-4 h-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary" />
                  <span className="text-sm text-muted-foreground">Thinking...</span>
                </div>
              </div>
            </div>
          )}
        </ScrollArea>

        {/* Quick Questions (only shown when no messages yet) */}
        {messages.length === 0 && (
          <div className="px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground mb-2">Quick questions to get started:</p>
            <div className="grid grid-cols-2 gap-2">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSend(question)}
                  className="text-left p-3 text-sm text-foreground bg-background border border-border rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask about refurbishment steps, safety, troubleshooting..."
              className="flex-1"
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
              disabled={isLoading}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleFileUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              disabled={isUploading}
              title="Upload a PDF to the Repair Manual Library and use it now"
            >
              {isUploading ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              ) : (
                <Paperclip className="w-5 h-5 text-muted-foreground" />
              )}
            </button>
            <Button onClick={() => handleSend()} size="icon" className="bg-primary hover:bg-primary/90" disabled={isLoading || !inputValue.trim()}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Context Footer */}
        <div className="px-4 py-2 bg-muted/30 border-t border-border">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-primary">
              <Paperclip className="w-3 h-3" />
              <span>Context: Phase {currentStep.phase} - {currentStep.title}</span>
            </div>
            {hasManual && (
              <span className="text-refurbish flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {manualTitle ? manualTitle : "Manual active"}
              </span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
